<template>
    <popupAnotacaoWrapper :show="true" :leftPosition="xPosition" :topPosition="yPosition" @onClose="onClose"
        @onSave="onSave" :previewRelatorioText="preview.annotationText">

        <template #title>
            {{ reportNode?.layout?.caption }}
            <template v-if="current.localizacao.dentes.length == 1">
                <span class="title__detail">(Dente {{ current.localizacao.dentes[0].text }})</span>
            </template>
        </template>

        <template #content>

            <div class="seletor" v-if="canSelectDentes">
                <section class="seletor__dentes" v-if="canSelectDentes">
                    <template v-if="tipoSelect.dente === 'multiple'">
                        <selectUI type="multiple" label="Dentes" labelPlacement="inside" placeholder="Selecione os dentes" 
                            :maxVisibleOptions="5" :minWidth="120" :panelMaxHeight="120"
                            :options="options.localizacao.dente.lista"
                            :defaultSelected="current.localizacao.dentes.map(dente => dente.id)"
                            @change="onChangeSelectedDentes">
                            <template #optionLabel="{ option }">
                                <reportLabelAnnotationItem
                                    :disabled="true"
                                    :annotation="{ id: option.value, reportText: option.label, color: option.color, localizacao: {} }" />
                            </template>
                        </selectUI>
                    </template>
                    <template v-else>
                        <selectUI type="single" label="Dente" labelPlacement="inside" placeholder="Selecione o dente"
                            :minWidth="120" :panelMaxHeight="120" :options="options.localizacao.dente.lista"
                            :defaultSelected="current.localizacao.dentes?.map(dente => dente.id)?.[0] || null"
                            @change="onChangeSelectedDentes([$event])">
                            <template #optionLabel="{ option }">
                                <reportLabelAnnotationItem
                                    :disabled="true"
                                    :annotation="{ id: option.value, reportText: option.label, color: option.color, localizacao: {} }" />
                            </template>
                        </selectUI>
                    </template>
                </section>

                <section class="seletor__regioes-anatomicas" v-if="canSelectRegiaoAnatomica">
                    <selectUI type="single" label="Região anatômica" labelPlacement="inside"
                        placeholder="Selecione a região" :maxVisibleOptions="5" :minWidth="150" :panelMaxHeight="120"
                        :options="options.localizacao.regiaoAnatomica.lista"
                        :defaultSelected="current.localizacao.regioesAnatomicas?.map(regiao => regiao.id)?.[0] || null"
                        @change="onChangeSelectedRegiaoAnatomica">
                        <template #optionLabel="{ option }">
                            <template v-if="option.value">
                                <reportLabelAnnotationItem
                                    :disabled="true"
                                    :annotation="{ id: option.value, reportText: option.label, color: option.color, localizacao: {} }" />
                            </template>
                            <template v-else>
                                <span class="option-label">{{ option.label }}</span>
                            </template>
                        </template>
                    </selectUI>
                </section>
            </div>

            <div class="marcacoes">

                <flatCard class="marcacoes__localizacao-interno" v-if="canSelectLocations">

                    <div class="dente-interno" v-if="activeDente">
                        <template v-if="activeTab.internalLocation === 'face'">
                            <selectFacesDente :size="110" :tipoSelect="tipoSelect.face" :numeroDente="activeDenteNumero"
                                :options="options.localizacao.dente.faces" :defaultSelected="selectedDenteFaces"
                                @selectChange="onSelectFacesDenteInterno">
                            </selectFacesDente>
                        </template>
                        <template v-if="activeTab.internalLocation === 'porcao'">
                            <selectPorcoesDente :size="110" :tipoSelect="tipoSelect.porcao"
                                :numeroDente="activeDenteNumero" :options="options.localizacao.dente.porcoes"
                                :defaultSelected="selectedDentePorcoes" @selectChange="onSelectPorcoesDenteInterno">
                            </selectPorcoesDente>
                        </template>

                        <section class="dente-interno__tabs">
                            <secondaryButton class="button" v-if="canSelectFacesDente"
                                :variant="activeTab.internalLocation === 'face' ? 'solid' : 'outline'"
                                @click="onSelectInternalLocationTab('face')">
                                Face
                                <template v-if="selectedDenteFaces.length > 0">
                                    <span class="counter">{{ selectedDenteFaces.length }}</span>
                                </template>
                            </secondaryButton>
                            <secondaryButton class="button" v-if="canSelectPortionsDente"
                                :variant="activeTab.internalLocation === 'porcao' ? 'solid' : 'outline'"
                                @click="onSelectInternalLocationTab('porcao')">
                                Porção
                                <template v-if="selectedDentePorcoes.length > 0">
                                    <span class="counter">{{ selectedDentePorcoes.length }}</span>
                                </template>
                            </secondaryButton>
                        </section>
                    </div>

                    <span class="help-text">
                        Clique nas regiões do dente para selecionar ou desmarcar
                    </span>
                </flatCard>

                <flatCard v-if="options.qualificadores.length > 0" class="marcacoes__qualificadores"
                    title="Qualificador">
                    <section class="list">
                        <div v-for="qualificador in options.qualificadores" :key="qualificador.type" class="item">
                            <checkboxUI :checked="current.qualificadores.some(q => q.type === qualificador.type)"
                                @change="(checked) => onSelectQualificador(qualificador, checked)">
                                <template #label>
                                    <span class="item__label">
                                        {{ qualificador.label }}
                                    </span>
                                </template>
                            </checkboxUI>
                        </div>
                    </section>
                </flatCard>

                <flatCard v-if="options.especificacao.externo.nome && options.especificacao.externo.items.length > 0"
                    class="marcacoes__especificacao-externo" :title="options.especificacao.externo.nome">
                    <inputRadioGroupUI :withDeselect="canDeselectEspecificacaoExterno"
                        :value="current.localizacao.especificacao" :options="options.especificacao.externo.items"
                        @change="(newValue) => onSelectEspecificacaoExterno(newValue)">
                    </inputRadioGroupUI>
                </flatCard>
            </div>
        </template>
    </popupAnotacaoWrapper>
</template>

<script>
import { gConsts } from '@/globals/gConsts';
import { nodes } from '@/globals/nodes';
import { utils } from '@/globals/utils';
import { evtBus } from '@/main';
import { reportUtils } from '@/features/report/lib/reportUtils';
import { anotacaoUtils } from '@/features/anotacoes/lib/AnotacaoUtils';
import { toothUtils } from '@/features/tooth/lib/toothUtils';
import { mapActions, mapGetters } from 'vuex';
import _ from 'lodash';


import SecondaryButton from '@/components/ui/button/secondaryButton.vue';
import FlatCard from '@/components/ui/card/flatCard.vue';
import checkboxUI from '@/components/ui/checkbox.vue';
import inputRadioGroupUI from '@/components/ui/input/inputRadioGroup.vue';
import selectUI from '@/components/ui/select/select.vue';

import popupAnotacaoWrapper from './popupAnotacaoWrapper.vue';

import selectPorcoesDente from '@/features/anotacoes/components/select/selectPorcoesDente.vue';
import selectFacesDente from '@/features/anotacoes/components/select/selectFacesDente.vue';
import reportLabelAnnotationItem from '@/features/report/components/text/reportLabelAnnotationItem.vue';

import { ReportNode, LabelAnnotation, LabelAnnotationLocation } from '@/features/report/models';

export default {
    name: 'popupAnotacao',
    components: {
        SecondaryButton,
        FlatCard,
        checkboxUI,
        inputRadioGroupUI,
        selectPorcoesDente,
        selectFacesDente,
        selectUI,
        popupAnotacaoWrapper,
        reportLabelAnnotationItem,
    },
    props: {
        xPosition: {
            type: Number,
            default: 0,
        },
        yPosition: {
            type: Number,
            default: 0,
        },
        reportNode: {
            type: ReportNode,
            required: true,
        },
        annotation: {
            type: LabelAnnotation,
            required: true,
        }
    },

    data() {
        return {
            options: {
                localizacao: {
                    dente: {
                        lista: [],
                        faces: [],
                        porcoes: [],
                    },
                    regiaoAnatomica: {
                        lista: [],
                    },
                },
                especificacao: {
                    externo: {
                        nome: null,
                        tipo: null,
                        items: [],
                    }
                },
                qualificadores: []
            },
            tipoSelect: {
                dente: 'multiple',
                face: 'coroa',
                porcao: 'raiz',
            },
            activeTab: {
                internalLocation: 'face', // face ou porcao
            },
            current: {
                localizacao: {
                    especificacao: this.annotation.localizacao.especificacao || null,
                    regioesAnatomicas: _.cloneDeep(this.annotation.localizacao.regioesAnatomicas || []),
                    dentes: _.cloneDeep(this.annotation.localizacao.dentes || []),
                    especificacaoInterna: this.annotation.localizacao.especificacaoInterna || null,
                },
                qualificadores: _.cloneDeep(this.annotation.qualificadores || []),
            },
            preview: {
                annotationText: '',
                labelReportLine: '',
            }
        }
    },

    watch: {
        show: {
            handler(newVal) {
                this.showPopup = newVal;
            },
            immediate: true
        }
    },

    computed: {
        ...mapGetters([
            'GET_waZoomScale',
            'GET_jobTools',
            'GET_toothRegionRect',
            'GET_regioesAnatomicasTools',
            'GET_teethSegTools_sorted',
            'GET_dentesAusentesNodes',
            'GET_dentesNodes'
        ]),
        canSelectLocations() {
            if (this.current.localizacao.dentes.length > 0) {
                return this.canSelectFacesDente || this.canSelectPortionsDente;
            }
            return false;
        },
        canSelectDentes() {
            // return [gConsts.node.report.annotation.types.TOOTH, gConsts.node.report.annotation.types.RTOOTH].includes(this.reportNode?.annotation?.type);
            return [
                gConsts.node.report.annotation.types.TOOTH,
                gConsts.node.report.annotation.types.RTOOTH,
                gConsts.node.report.annotation.types.ANAT_RTOOTH,
                gConsts.node.report.annotation.types.PROX_ANAT_RTOOTH
            ].includes(this.reportNode?.annotation?.type);
        },
        canSelectRegiaoAnatomica() {
            return [
                gConsts.node.report.annotation.types.ANAT_ONLY,
                gConsts.node.report.annotation.types.ANAT_RTOOTH,
                gConsts.node.report.annotation.types.PROX_ANAT_RTOOTH
            ].includes(this.reportNode?.annotation?.type);
        },
        canSelectFacesDente() {
            return this.options.localizacao.dente.faces.length > 0;
        },
        canSelectPortionsDente() {
            return this.options.localizacao.dente.porcoes.length > 0;
        },
        canDeselectEspecificacaoExterno() {
            return [gConsts.elem.location.especificacao.group.types.SITUACAO].includes(this.options.especificacao.externo.tipo);
        },
        activeDente() {
            return this.current.localizacao?.dentes?.[0];
        },
        activeDenteNumero() {
            return Number(this.activeDente?.text);
        },
        selectedDenteFaces() {
            return this.activeDente?.faces?.map(face => face.split('__')?.[1] || face);
        },
        selectedDentePorcoes() {
            return this.activeDente?.porcoes?.map(porcao => porcao.split('__')?.[1] || porcao);
        },
    },

    methods: {
        ...mapActions([
            'ACT_reportNodes_updateLabelAnnotation'
        ]),

        onChangeSelectedDentes(denteIds) {
            const selectedInternalLocations = {
                faces: this.activeDente ? [...this.activeDente.faces] : [],
                porcoes: this.activeDente ? [...this.activeDente.porcoes] : [],
            }
            const dentesOptions = this.options.localizacao.dente.lista
                .map(option => option.children ? option.children : [option])
                .flat();
            this.current.localizacao.dentes = dentesOptions
                .filter(denteOption => denteIds.includes(denteOption.value))
                .map(denteOption => {
                    return {
                        id: denteOption.value,
                        text: denteOption.label,
                        color: denteOption.color,
                        faces: selectedInternalLocations.faces,
                        porcoes: selectedInternalLocations.porcoes,
                    }
                });

            this.refreshMarcacaoText();
        },

        onChangeSelectedRegiaoAnatomica(regiaoAnatomicaId) {
            if (!regiaoAnatomicaId) {
                this.current.localizacao.regioesAnatomicas = [];
            } else {
                const regioesOptions = this.options.localizacao.regiaoAnatomica.lista
                    .map(option => option.children ? option.children : [option])
                    .flat();
                this.current.localizacao.regioesAnatomicas = regioesOptions
                    .filter(regiaoOption => regiaoAnatomicaId == regiaoOption.value)
                    .map(regiaoOption => {
                        return {
                            id: regiaoOption.value,
                            text: regiaoOption.label,
                            color: regiaoOption.color,
                        };
                    });

            }

            this.refreshMarcacaoText();
        },

        onSelectInternalLocationTab(tab) {
            this.activeTab.internalLocation = tab;
        },

        onSelectFacesDenteInterno(faces) {
            if (!this.activeDente)
                return;
            // this.activeDente.faces = [...faces];
            const selectedFaces = faces.map(face => `${this.tipoSelect.face}__${face}`)
            this.current.localizacao.dentes.forEach(dente => {
                dente.faces = [...selectedFaces];
            });
            this.refreshMarcacaoText();
        },

        onSelectPorcoesDenteInterno(porcoes) {
            if (!this.activeDente)
                return;
            // this.activeDente.porcoes = [...porcoes];
            const selectedPorcoes = porcoes.map(porcao => `${this.tipoSelect.porcao}__${porcao}`)
            this.current.localizacao.dentes.forEach(dente => {
                dente.porcoes = [...selectedPorcoes];
            });
            this.refreshMarcacaoText();
        },

        onSelectQualificador(qualificadorItem, checked) {
            if (checked) {
                this.current.qualificadores.push(qualificadorItem);
            } else {
                this.current.qualificadores = this.current.qualificadores.filter(qualificador => qualificador.type !== qualificadorItem.type);
            }
            this.refreshMarcacaoText();
        },

        onSelectEspecificacaoExterno(newValue) {
            this.current.localizacao.especificacao = newValue;
            this.refreshMarcacaoText();
        },

        onClose() {
            this.$emit('onClose');
        },

        onSave() {
            console.log('onSave: ', this.preview.annotationText);
            this.$emit('onSave');
            this.ACT_reportNodes_updateLabelAnnotation({
                labelId: this.reportNode.id,
                annotation: {
                    id: this.annotation.id,
                    toolId: this.annotation.toolId,
                    localizacao: this.current.localizacao,
                    qualificadores: this.current.qualificadores
                }
            });
            this.onClose();
        },

        refreshMarcacaoText() {
            const labelAnnotation = new LabelAnnotation(
                this.annotation.tipo,
                this.annotation.toolId,
                new LabelAnnotationLocation(
                    this.current.localizacao.especificacao,
                    _.cloneDeep(this.current.localizacao.regioesAnatomicas),
                    _.cloneDeep(this.current.localizacao.dentes),
                    this.current.localizacao.especificacaoInterna
                ),
                _.cloneDeep(this.current.qualificadores).map(qualificador => ({
                    label: qualificador.label,
                    type: qualificador.type,
                    format: qualificador.format,
                    text: qualificador.text
                }))
            );

            console.debug('refreshMarcacaoText: ', labelAnnotation);

            this.preview.annotationText = reportUtils.getAnnotationsText([labelAnnotation]);
            // this.preview.labelReportLine = reportUtils.getInitialReportText(this.reportNode, [labelAnnotation]);

            evtBus.$emit('evt_popupAnotacao_onOpen', {
                annotation: labelAnnotation,
            });
        },

        mapToolToSelectOption(tool) {
            const labelNode = nodes.getNode(tool.label.id);
            return this.mapNodeToSelectOption(labelNode);
        },

        mapNodeToSelectOption(node) {
            const color = gConsts.ui.prettyColors[node?.layout?.color?.toLowerCase()] || node?.layout?.color;
            return {
                value: node.id,
                label: node.layout.caption,
                color: color,
            };
        },

        populateDentesOptions() {
            if (!this.canSelectDentes)
                return;

            let denteIdsProximos = []
            const annotationTool = this.GET_jobTools.find(tool => tool.id === this.annotation.toolId);
            if (annotationTool) {
                if (annotationTool.locations.regions?.list?.length > 0) {
                    denteIdsProximos = annotationTool.locations.regions.list.map(regiao => regiao);
                } else if (annotationTool.dente) {
                    denteIdsProximos = [annotationTool.dente];
                }
                // Uso o dobro da distancia normal para dar possibilidade de outros dentes.
                // const maxDistance = this.GET_toothRegionRect.width * 2;
                // const position = annotationTool.coords.pts[annotationTool.coords.pts.length - 1];
                // const denteToolsProximos = utils.findCloseDenteToolsFromPosition(this.GET_teethSegTools_sorted, position, maxDistance);
                // // console.log('denteToolsProximos: ', denteToolsProximos);
                // denteIdsProximos = denteToolsProximos.map(denteTool => denteTool.label.id);
            } else if (this.annotation.localizacao?.dentes?.length > 0) {
                denteIdsProximos = this.annotation.localizacao.dentes.map(dente => dente.id);
            }

            if (denteIdsProximos.length > 0) {
                const closestDenteId = denteIdsProximos[0];
                const toothDetails = toothUtils.getToothDetailsByLabelId(closestDenteId);
                const closestDenteOrderIndex = toothUtils.getToothOrderIndex(toothDetails.numero, 'complete');
                // Ordeno os dentes mais proximos da posicao da arcada do dente mais proximo
                // O dente mais proximo ficara no topo da lista
                const closeDenteNodes = this.GET_dentesNodes
                    .sort((a,b) => {
                        const aOrderIndex = toothUtils.getToothOrderIndex(+a.layout.caption, 'complete');
                        const bOrderIndex = toothUtils.getToothOrderIndex(+b.layout.caption, 'complete');
                        return Math.abs(aOrderIndex - closestDenteOrderIndex) - Math.abs(bOrderIndex - closestDenteOrderIndex);
                    }).slice(0, 5);
                    
                if (this.annotation.localizacao?.dentes?.length > 0) {
                    this.annotation.localizacao.dentes.forEach(dente => {
                        const denteNode = this.GET_dentesNodes.find(denteNode => denteNode.id === dente.id);
                        closeDenteNodes.splice(closeDenteNodes.indexOf(denteNode), 1);
                        closeDenteNodes.unshift(denteNode);
                    })
                }
                this.options.localizacao.dente.lista = [
                    {
                        value: 'proximos',
                        label: 'Próximos',
                        children: closeDenteNodes.map(this.mapNodeToSelectOption),
                    },
                    {
                        value: 'outros',
                        label: 'Outros Dentes',
                        children: this.GET_dentesNodes
                            .filter(dente => !closeDenteNodes.some(denteNode => denteNode.id === dente.id))
                            .map(this.mapNodeToSelectOption),
                    }
                ];
            } else {
                this.options.localizacao.dente.lista = this.GET_dentesNodes
                    .map(this.mapNodeToSelectOption);
            }

            this.tipoSelect.dente = [
                gConsts.node.report.annotation.types.TOOTH,
            ].includes(this.reportNode?.annotation?.type) ? 'single' : 'multiple';
        },

        populateRegiaoAnatomicaOptions() {
            if (!this.canSelectRegiaoAnatomica)
                return;

            let regiaoToolsProximos = []
            const annotationTool = this.GET_jobTools.find(tool => tool.id === this.annotation.toolId);
            if (annotationTool) {
                // Uso o dobro da distancia normal para dar possibilidade de outros dentes.
                const maxDistance = this.GET_toothRegionRect.width * 2;
                regiaoToolsProximos = utils.findCloseToolsFromTool(this.GET_regioesAnatomicasTools, annotationTool, maxDistance);
                // console.log('denteToolsProximos: ', denteToolsProximos);
            }

            if (regiaoToolsProximos.length > 0) {
                this.options.localizacao.regiaoAnatomica.lista = [
                    {
                        value: 'proximos',
                        label: 'Próximos',
                        children: regiaoToolsProximos.map(this.mapToolToSelectOption),
                    },
                    {
                        value: 'outros',
                        label: 'Outras Regiões',
                        children: this.GET_regioesAnatomicasTools
                            .filter(tool => !regiaoToolsProximos.some(regiaoTool => regiaoTool.label.id === tool.label.id))
                            .map(this.mapToolToSelectOption),
                    }
                ];
            } else {
                this.options.localizacao.regiaoAnatomica.lista = this.GET_regioesAnatomicasTools
                    .map(this.mapToolToSelectOption);
            }

            const canSelectNone = [
                gConsts.node.report.annotation.types.PROX_ANAT_RTOOTH
            ].includes(this.reportNode?.annotation?.type);

            if (canSelectNone) {
                this.options.localizacao.regiaoAnatomica.lista.unshift({
                    value: null,
                    label: 'Nenhuma região',
                    color: null,
                });
            }
        },

        populateOptions() {
            if (!this.reportNode)
                return;

            this.populateDentesOptions();
            this.populateRegiaoAnatomicaOptions();

            this.options.qualificadores = anotacaoUtils.getQualificadoresByLabelId(this.reportNode.id);
            const localizacaoInternaInfo = anotacaoUtils.getLocalizacaoInternaInfoByLabelId(this.reportNode.id);
            if (localizacaoInternaInfo) {
                // console.log('localizacaoInternaInfo: ', localizacaoInternaInfo);
                this.options.localizacao.dente.faces = localizacaoInternaInfo.localizacao.face.lista;
                this.tipoSelect.face = localizacaoInternaInfo.localizacao.face.tipo;
                this.options.localizacao.dente.porcoes = localizacaoInternaInfo.localizacao.porcao.lista;
                this.tipoSelect.porcao = localizacaoInternaInfo.localizacao.porcao.tipo;
            }

            const especificacaoGrupo = anotacaoUtils.getEspecificacaoGrupoByLabelId(this.reportNode.id);
            if (especificacaoGrupo) {
                // console.log('especificacaoGrupo: ', especificacaoGrupo);
                this.options.especificacao.externo.nome = especificacaoGrupo.nome;
                this.options.especificacao.externo.tipo = especificacaoGrupo.tipo;
                this.options.especificacao.externo.items = especificacaoGrupo.items || [];
            }

            this.activeTab.internalLocation = this.canSelectFacesDente ? 'face' : 'porcao';
        },

    },

    mounted() {
        setTimeout(() => {
            this.populateOptions();
            this.refreshMarcacaoText();
        }, 100);

        evtBus.$on('evt_popupAnotacao_close', () => {
            this.onClose();
        });
    }
}
</script>

<style scoped>
.popup-anotacao .header__content .title__detail {
    font-size: var(--text-sm);
    font-weight: 400;
    color: var(--color-dark-gray);
}

.seletor {
    display: flex;
    align-items: center;
    gap: var(--spacing-md);
}

.seletor__regioes-anatomicas {
    min-width: 250px;
}

.seletor__dentes :deep(span.report-label-annotation-item > .text),
.seletor__regioes-anatomicas :deep(span.report-label-annotation-item > .text) {
    line-height: var(--leading-heading);
}

.marcacoes {
    display: flex;
    gap: var(--spacing-md);
}

.marcacoes .dente-tabs {
    display: flex;
    width: max-content;
    gap: var(--spacing-sm);
    padding: var(--spacing-xs) var(--spacing-sm);
    border: 1px solid var(--border-primary);
    border-radius: var(--radius-lg);
    background-color: var(--background-white);
}

.marcacoes .dente-tabs .button {
    text-align: center;
    align-items: center;
    justify-content: center;
    font-size: var(--text-sm);
    line-height: var(--leading-normal);
    width: 32px;
    padding: var(--spacing-xs) var(--spacing-sm);
}

.marcacoes .dente-tabs .button[data-selected="true"] {
    border-color: var(--color-primary);
    background-color: var(--color-primary);
    color: var(--color-white);
}

.marcacoes__localizacao-interno {
    flex: 1;
    gap: var(--spacing-sm);
}

.marcacoes__localizacao-interno .dente-interno {
    min-width: 270px;
    position: relative;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    gap: var(--spacing-sm);
}

.marcacoes__localizacao-interno .dente-interno__tabs {
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
}

.marcacoes__localizacao-interno .dente-interno__tabs .button {
    position: relative;
    text-align: center;
    align-items: center;
    justify-content: center;
    font-size: var(--text-xs);
    line-height: 1;
}

.marcacoes__localizacao-interno .dente-interno__tabs .button[data-selected="true"] {
    background-color: var(--color-primary);
    color: var(--color-white);
}

.marcacoes__localizacao-interno .dente-interno__tabs .counter {
    position: absolute;
    top: -8px;
    right: -8px;
    font-size: var(--text-xxs);
    color: var(--color-dark-gray);
    background-color: var(--background-gray);
    border-radius: var(--radius-circle);
    border: 1px solid var(--border-primary);
    width: 14px;
    height: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.marcacoes__localizacao-interno .help-text {
    align-self: center;
    max-width: 200px;
    font-size: var(--text-xs);
    line-height: 1;
    color: var(--color-dark-gray);
}

.marcacoes__qualificadores {
    flex: 1;
    min-width: 170px;
}

.marcacoes__qualificadores>.list {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    flex-direction: column;
    list-style: none;
    padding: 0;
    margin: 0;
    padding-left: var(--spacing-sm);
}

.marcacoes__qualificadores .item__label,
.marcacoes__especificacao-externo .item__label {
    text-transform: capitalize;
    font-size: var(--text-xs);
    line-height: 1;
    font-weight: 500;
    text-align: left;
}

.marcacoes__especificacao-externo {
    flex: 1;
    min-width: 250px;
}

.marcacoes__especificacao-externo :deep(.radio-group) {
    display: flex;
    padding-left: var(--spacing-sm);
    /* flex-direction: row;
    flex-wrap: wrap;
    gap: var(--spacing-sm); */
}

.marcacoes__especificacao-externo :deep(.radio-group .radio-group__option) {
    flex: 1;
}
</style>
